import { Component, DestroyRef, inject, output, OutputEmitterRef, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '@auth0/auth0-angular';
import { combineLatest, filter, take, timer, switchMap, takeWhile, catchError, EMPTY } from 'rxjs';

import { UserStore } from '../../../../core/components/header/models/internal/user.store';
import { CnpjService } from '../../../../core/services/common/cnpj.service';
import { PaymentApiService } from './services/payment.service';
import { PaymentApiRequest } from './models/payment.resquest';
import { UserService } from '../../../../core/services/common/user.service';
import { EventsService } from '../../../../core/services/common/events.service';

@Component({
  selector: 'app-modal-plano-otimiza',
  standalone: true,
  imports: [],
  templateUrl: './modal-plano-otimiza.component.html',
  styleUrl: './modal-plano-otimiza.component.scss'
})
export class ModalPlanoOtimizaComponent {
  private readonly paymentService = inject(PaymentApiService);
  private readonly userStore = inject(UserStore);
  private readonly userService = inject(UserService);
  private readonly cnpjService = inject(CnpjService);
  private readonly auth0 = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly eventService = inject(EventsService)

  public closeModal: OutputEmitterRef<void> = output<void>();
  public isLoading = signal(false);
  public modalStep = signal(1);
  public paymentStatus = signal<string>('Pendente');

  public requestPaymentLink(): void {
    this.eventService.eventTrack('CLICK_ASSINAR_OTIMIZA');

    if (this.isLoading()) return;

    this.isLoading.set(true);
    this.modalStep.set(2);

    const paymentWindow = window.open('', '_blank');

    combineLatest([
      this.auth0.user$.pipe(take(1)),
      this.cnpjService.getCnpjs().pipe(
        filter(response => Array.isArray(response?.data) && response.data.length > 0),
        take(1)
      )
    ]).subscribe(([auth0User, cnpjsResponse]) => {
      const selectedCnpj = this.userStore.user().selectedCnpj
        || cnpjsResponse?.data?.[0]?.cnpj
        || '';

      const payload: PaymentApiRequest = {
        name: auth0User?.name ?? '',
        email: auth0User?.email ?? this.userStore.user().email,
        cnpj: selectedCnpj
      };

      this.paymentService.requestPaymentLink(payload).subscribe({
        next: (response) => {
          if (paymentWindow) {
            paymentWindow.location.href = response.url;
          } else {
            window.open(response.url, '_blank');
          }
          if (response.id) {
            this.startPaymentPolling(response.id);
          } else {
            this.isLoading.set(false);
          }
        },
        error: (err) => {
          console.error('[modal-plano-otimiza] erro:', err.status, err.error);
          if (paymentWindow) paymentWindow.close();
          this.isLoading.set(false);
        }
      });
    });
  }

  public finalizePayment(): void {
    this.closeModal.emit();
    this.userService.logout();
  }

  private startPaymentPolling(transactionId: string): void {
    timer(0, 30000).pipe(
      switchMap(() => this.paymentService.getPaymentStatus(transactionId)),
      takeWhile(statusResponse => statusResponse.status !== 'paid', true),
      takeUntilDestroyed(this.destroyRef),
      catchError(error => {
        console.error('Erro no polling:', error);
        this.isLoading.set(false);
        return EMPTY;
      })
    ).subscribe({
      next: (statusResponse) => {
        this.paymentStatus.set(statusResponse.status);
        if (statusResponse.status === 'paid') {
          this.isLoading.set(false);
          this.modalStep.set(3);
        } else if (statusResponse.status === 'failed' || statusResponse.status === 'canceled') {
          console.error('Pagamento falhou ou foi cancelado.');
          this.isLoading.set(false);
        }
      },
      complete: () => { }
    });
  }
}
